import { useCallback, useEffect, useState } from "react";
import { Alert, Button, Divider, Form, InputNumber, Space, Spin, Typography } from "antd";
import { ReloadOutlined, SaveOutlined } from "@ant-design/icons";
import { configOptionApi, GeneralSettings } from "../../services/configOption.api";
import { clpInputFormatter, clpInputParser } from "../../utils/clpInput";
import { loadIvaRate, DEFAULT_IVA_PORCENTAJE } from "../../utils/iva";

const { Text, Title } = Typography;

/**
 * PreciosGuardTab — pestaña "Precios e IVA" de /configuracion.
 *
 * Configura los ajustes generales financieros:
 *
 * 1. `umbralPrecioBajo`: monto en CLP por debajo del cual fijar un "precio
 *    por noche base" en el calendario exige una SEGUNDA confirmación.
 *    Red anti-error de tipeo (pedido del owner): previene que admin/
 *    superadmin empujen al calendario precios absurdamente bajos por
 *    equivocación. GENÉRICO para toda propiedad — distinto del "precio
 *    mínimo por noche acordado" que se configura por propiedad. Lo consume
 *    el MetaEditModal del calendario. Ver docs/modulo-calendario.md.
 *
 * 2. `ivaPorcentaje` (round 2026-07-03, pedido del owner): tasa de IVA (%)
 *    usada por TODO cálculo de IVA del sistema — IVA de comisiones AF,
 *    desglose neto/IVA/total de los subítems de limpieza, lavados, etc.
 *    Antes era un 0.19 hardcodeado; ahora frontend (utils/iva.ts) y backend
 *    (utils/ivaRate.js) lo leen de este ajuste. Default 19.
 *    Ver docs/flujo-financiero.md §8.
 */

// El singleton `general-settings` trae más claves que las de esta pestaña
// (desde 2026-07-28 también los interruptores de correo). Nos quedamos SOLO con
// las dos nuestras: si volcáramos el objeto entero al form, "Restaurar valores
// por defecto" arrastraría los interruptores de correo a su default y el
// Guardar de acá volvería a encender correos que el admin apagó en otra
// pestaña — un efecto que nadie esperaría de un botón de precios.
const soloPrecios = (s: GeneralSettings): GeneralSettings => ({
  umbralPrecioBajo: s.umbralPrecioBajo,
  ivaPorcentaje: s.ivaPorcentaje,
});

export default function PreciosGuardTab() {
  const [form] = Form.useForm<GeneralSettings>();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState<GeneralSettings | null>(null);
  const [defaults, setDefaults] = useState<GeneralSettings>({
    umbralPrecioBajo: 40000,
    ivaPorcentaje: DEFAULT_IVA_PORCENTAJE,
  });

  const loadSettings = useCallback(async () => {
    setLoading(true);
    const res = await configOptionApi.getGeneralSettings();
    if (res) {
      setSaved(res.data);
      setDefaults(soloPrecios(res.defaults));
      form.setFieldsValue(soloPrecios(res.data));
    } else {
      // Backend aún no disponible: usamos los defaults del frontend.
      form.setFieldsValue({
        umbralPrecioBajo: 40000,
        ivaPorcentaje: DEFAULT_IVA_PORCENTAJE,
      });
    }
    setLoading(false);
  }, [form]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const handleSave = async () => {
    let values: GeneralSettings;
    try {
      values = await form.validateFields();
    } catch {
      // Validación fallida: AntD ya muestra el error inline en el campo.
      return;
    }
    setSaving(true);
    try {
      const res = await configOptionApi.updateGeneralSettings(soloPrecios(values));
      if (res) {
        setSaved(res.data);
        // Refrescar el cache del módulo utils/iva.ts en ESTA sesión para que
        // los formularios (propiedades, lavandería, calendario) usen la tasa
        // nueva sin recargar la página.
        void loadIvaRate(true);
      }
    } finally {
      setSaving(false);
    }
  };

  const handleRestoreDefaults = () => {
    form.setFieldsValue(defaults);
  };

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: 48 }}>
        <Spin />
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 640 }}>
      <Form
        form={form}
        layout="vertical"
        initialValues={{ umbralPrecioBajo: 40000, ivaPorcentaje: DEFAULT_IVA_PORCENTAJE }}
      >
        <Title level={5}>IVA</Title>
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="Tasa de IVA del sistema"
          description={
            <>
              <div>
                Este porcentaje se usa en <b>todos los cálculos de IVA</b> del
                sistema: el IVA de las comisiones de Aloja Fácil por arriendo,
                el desglose neto/IVA/total de los subítems de limpieza
                (transporte, materiales, lavandería) y las conversiones
                neto ↔ con IVA de lavandería.
              </div>
              <div style={{ marginTop: 6 }}>
                Cambiarlo afecta los <b>cálculos nuevos</b> (crear/editar/
                recalcular) — los montos ya guardados <b>no</b> se recalculan
                solos. Default: {DEFAULT_IVA_PORCENTAJE}%.
              </div>
            </>
          }
        />
        <Form.Item
          name="ivaPorcentaje"
          label="IVA (%)"
          tooltip="Tasa de IVA vigente en Chile. Se aplica a todo cálculo de IVA del sistema. Default: 19."
          rules={[
            { required: true, message: "Ingresa el porcentaje de IVA" },
            {
              type: "number",
              min: 0,
              max: 100,
              message: "Entre 0 y 100",
            },
          ]}
        >
          <InputNumber
            style={{ width: "100%", maxWidth: 320 }}
            min={0}
            max={100}
            step={0.5}
            controls={false}
            addonAfter="%"
            placeholder="19"
          />
        </Form.Item>

        <Divider />

        <Title level={5}>Umbral de precio bajo</Title>
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="¿Para qué sirve este umbral?"
          description={
            <>
              <div>
                Es una <b>red de seguridad contra errores de tipeo</b>. Cuando
                alguien fija el <b>precio por noche base</b> de una propiedad en el
                calendario y escribe un valor <b>menor a este monto</b>, el sistema
                pide una <b>segunda confirmación</b> antes de aplicarlo, para evitar
                que un admin o superadmin publique por error un precio
                absurdamente bajo (p. ej. escribir «4.000» en vez de «40.000»).
              </div>
              <div style={{ marginTop: 6 }}>
                Es <b>genérico para todas las propiedades</b>. No lo confundas con
                el <b>«Precio mínimo por noche acordado»</b> de cada propiedad —
                ese es el piso pactado con cada propietario y se configura en la
                ficha de la propiedad.
              </div>
            </>
          }
        />
        <Form.Item
          name="umbralPrecioBajo"
          label="Pedir confirmación si el precio por noche base es menor a (CLP)"
          tooltip="Por debajo de este monto, fijar un precio por noche en el calendario exige una confirmación adicional. Default: 40.000 CLP."
          rules={[
            { required: true, message: "Ingresa un monto" },
            {
              type: "number",
              min: 0,
              max: 100000000,
              message: "Entre 0 y 100.000.000",
            },
          ]}
        >
          <InputNumber
            style={{ width: "100%", maxWidth: 320 }}
            min={0}
            max={100000000}
            step={1000}
            controls={false}
            formatter={clpInputFormatter}
            parser={clpInputParser}
            placeholder="40.000"
          />
        </Form.Item>

        <Space>
          <Button
            type="primary"
            icon={<SaveOutlined />}
            onClick={handleSave}
            loading={saving}
          >
            Guardar
          </Button>
          <Button icon={<ReloadOutlined />} onClick={handleRestoreDefaults}>
            Restaurar valores por defecto
          </Button>
          {saved && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              Guardado: IVA {saved.ivaPorcentaje ?? DEFAULT_IVA_PORCENTAJE}% ·
              umbral{" "}
              {new Intl.NumberFormat("es-CL", {
                style: "currency",
                currency: "CLP",
                maximumFractionDigits: 0,
              }).format(saved.umbralPrecioBajo)}
            </Text>
          )}
        </Space>
      </Form>
    </div>
  );
}
